/**
 * Analyzer Page - Split view: Newsletter preview + Link list
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { newsletterAPI, validationAPI } from '../services/api';

const classificationColors = {
  normal: { bg: 'bg-emerald-50', border: 'border-emerald-400', text: 'text-emerald-700', badge: 'bg-emerald-500' },
  warning: { bg: 'bg-red-50', border: 'border-red-400', text: 'text-red-800', badge: 'bg-red-600' },
  fake: { bg: 'bg-rose-50', border: 'border-rose-400', text: 'text-rose-800', badge: 'bg-rose-600' },
};

export const AnalyzerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const containerRef = useRef(null);

  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLink, setSelectedLink] = useState(null);
  const [validatingUrl, setValidatingUrl] = useState(null);
  const [validationResults, setValidationResults] = useState({});
  const [filterType, setFilterType] = useState('all'); // all, normal, warning, fake
  const [leftPanelWidth, setLeftPanelWidth] = useState(70); // percentage

  useEffect(() => {
    loadNewsletter();
  }, [id]);

  const loadNewsletter = async () => {
    try {
      setLoading(true);
      const { data } = await newsletterAPI.getById(id);
      setNewsletter(data);
    } catch (err) {
      setError('Failed to load newsletter');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateLink = async (url) => {
    try {
      setValidatingUrl(url);
      const { data } = await validationAPI.checkUrl(url);
      setValidationResults((prev) => ({ ...prev, [url]: data }));
    } catch (err) {
      setValidationResults((prev) => ({
        ...prev,
        [url]: { error: 'Validation failed', status: null },
      }));
    } finally {
      setValidatingUrl(null);
    }
  };

  // Handle resize of split panels
  const handleMouseDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = leftPanelWidth;
    const containerWidth = containerRef.current?.offsetWidth || 1000;

    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaPercent = (deltaX / containerWidth) * 100;
      const newWidth = Math.max(30, Math.min(80, startWidth + deltaPercent));
      setLeftPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const highlightLinkInPreview = (link) => {
    if (!iframeRef.current?.contentWindow) return;

    try {
      const iframeDoc = iframeRef.current.contentWindow.document;
      // Remove previous highlights
      iframeDoc.querySelectorAll('[data-link-highlight]').forEach((el) => {
        el.removeAttribute('data-link-highlight');
        el.style.outline = '';
      });

      // Find and highlight the link (best effort - search by URL)
      const elements = iframeDoc.querySelectorAll('a, [onclick], [data-href]');
      elements.forEach((el) => {
        const href = el.href || el.getAttribute('data-href') || el.getAttribute('onclick') || '';
        if (href.includes(link.url)) {
          el.setAttribute('data-link-highlight', 'true');
          el.style.outline = '3px solid #ff8c42';
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    } catch (err) {
      // CORS or other errors - silently fail
    }
  };

  const filteredLinks = newsletter?.links?.filter((link) => {
    if (filterType === 'all') return true;
    return link.classification === filterType;
  }) || [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading newsletter...</p>
        </div>
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Newsletter not found'}</p>
          <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-blue-600 text-white rounded">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const stats = {
    total: newsletter.links?.length || 0,
    normal: newsletter.links?.filter((l) => l.classification === 'normal').length || 0,
    warning: newsletter.links?.filter((l) => l.classification === 'warning').length || 0,
    fake: newsletter.links?.filter((l) => l.classification === 'fake').length || 0,
  };

  return (
    <div className="h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow px-6 py-4 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{newsletter.fileName}</h1>
          <p className="text-sm text-gray-600">
            {new Date(newsletter.uploadedAt).toLocaleString()}
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
        >
          ← Back
        </button>
      </header>

      {/* Statistics Bar */}
      <div className="bg-white px-6 py-3 border-b border-gray-200 flex gap-8 text-sm">
        <div>
          <span className="text-gray-600">Total Links: </span>
          <span className="font-bold text-gray-900">{stats.total}</span>
        </div>
        <div>
          <span className="text-gray-600">Normal: </span>
          <span className="font-bold text-emerald-600">{stats.normal}</span>
        </div>
        <div>
          <span className="text-gray-600">Warning: </span>
          <span className="font-bold text-red-600">{stats.warning}</span>
        </div>
        <div>
          <span className="text-gray-600">Fake: </span>
          <span className="font-bold text-rose-600">{stats.fake}</span>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div ref={containerRef} className="flex flex-1 overflow-hidden">
        {/* Left: Newsletter Preview */}
        <div 
          className="border-r border-gray-300 overflow-auto bg-gray-50 p-6"
          style={{ width: `${leftPanelWidth}%` }}
        >
          <div className="bg-white rounded shadow-sm">
            <iframe
              ref={iframeRef}
              srcDoc={newsletter.htmlContent}
              title="Newsletter Preview"
              className="w-full h-full border-none"
              style={{ minHeight: '1200px' }}
            />
          </div>
        </div>

        {/* Resize Handle */}
        <div
          onMouseDown={handleMouseDown}
          className="w-1 bg-gray-300 hover:bg-orange-500 cursor-col-resize transition-colors"
          title="Drag to resize panels"
        />

        {/* Right: Links List */}
        <div 
          className="bg-white flex flex-col border-l border-gray-300"
          style={{ width: `${100 - leftPanelWidth}%` }}
        >
          {/* Filter Buttons */}
          <div className="p-4 border-b border-gray-200 flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                filterType === 'all'
                  ? 'bg-gray-900 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({stats.total})
            </button>
            <button
              onClick={() => setFilterType('normal')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                filterType === 'normal'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
            >
              Normal ({stats.normal})
            </button>
            <button
              onClick={() => setFilterType('warning')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                filterType === 'warning'
                  ? 'bg-red-600 text-white'
                  : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              Warning ({stats.warning})
            </button>
            <button
              onClick={() => setFilterType('fake')}
              className={`px-3 py-1 rounded text-sm font-medium ${
                filterType === 'fake'
                  ? 'bg-rose-600 text-white'
                  : 'bg-rose-50 text-rose-700 hover:bg-rose-100'
              }`}
            >
              Fake ({stats.fake})
            </button>
          </div>

          {/* Links List */}
          <div className="flex-1 overflow-auto">
            {filteredLinks.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No links in this category</div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredLinks.map((link, idx) => {
                  const colors = classificationColors[link.classification];
                  const validation = validationResults[link.url];

                  return (
                    <div
                      key={idx}
                      className={`p-4 cursor-pointer transition ${
                        selectedLink === idx ? 'bg-orange-100 border-l-4 border-orange-600' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedLink(idx);
                        highlightLinkInPreview(link);
                      }}
                    >
                      {/* Classification Badge */}
                      <div className="flex items-start justify-between mb-2">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-bold text-white rounded ${colors.badge}`}
                        >
                          {link.classification.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">Score: {link.score || 0}/100</span>
                      </div>

                      {/* URL */}
                      <div className="mb-2">
                        <p className="text-xs font-semibold text-gray-600 mb-1">URL:</p>
                        <p className="text-sm text-blue-600 break-all font-mono hover:underline">
                          {link.url}
                        </p>
                      </div>

                      {/* Link Text */}
                      {link.innerHTML && (
                        <div className="mb-2">
                          <p className="text-xs font-semibold text-gray-600 mb-1">Text:</p>
                          <p className="text-sm text-gray-700 break-words">
                            {link.innerHTML.substring(0, 100)}
                            {link.innerHTML.length > 100 ? '...' : ''}
                          </p>
                        </div>
                      )}

                      {/* Suspicion Details */}
                      {link.suspicions && link.suspicions.length > 0 && (
                        <div className="mb-2 p-2 bg-red-50 rounded border border-red-200">
                          <p className="text-xs font-semibold text-red-700 mb-1">Suspicions:</p>
                          <ul className="text-xs text-red-600 space-y-1">
                            {link.suspicions.map((sus, i) => (
                              <li key={i}>• {sus}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Validation Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleValidateLink(link.url);
                        }}
                        disabled={validatingUrl === link.url}
                        className="w-full mt-2 px-2 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700 disabled:bg-gray-400"
                      >
                        {validatingUrl === link.url ? 'Validating...' : 'Validate URL'}
                      </button>

                      {/* Validation Result */}
                      {validation && (
                        <div
                          className={`mt-2 p-2 rounded text-xs ${
                            validation.error ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
                          }`}
                        >
                          {validation.error ? (
                            <p>{validation.error}</p>
                          ) : (
                            <>
                              <p>
                                <strong>Status:</strong> {validation.status}
                              </p>
                              {validation.redirects && validation.redirects.length > 0 && (
                                <p>
                                  <strong>Redirects:</strong> {validation.redirects.join(' → ')}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
